import React, { useState, useEffect } from 'react';
import {
    Box, Grid, Typography, TextField, Button, Paper, CircularProgress, 
    Snackbar, Alert, Divider, Card, CardContent, Avatar, useMediaQuery, 
    useTheme, Chip, IconButton, AlertTitle
} from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import InventoryIcon from '@mui/icons-material/Inventory';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../../lib/axios';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../../../redux/propertySlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';

const TiffinReturnForm = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const globalPropertyId = useSelector(selectGlobalProperty);
    const navigate = useNavigate();
    const location = useLocation();
    const [submitting, setSubmitting] = useState(false);
    const [orderData, setOrderData] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    
    const [formData, setFormData] = useState({
        returnQuantity: 0,
        returnRemark: '',
        returnLocation: '',
        returnTime: dayjs()
    });

    useEffect(() => {
        const state = location.state;
        if (state && state.mode === 'return' && state.orderData) {
            setOrderData(state.orderData);
            
            setFormData(prev => ({
                ...prev,
                returnQuantity: state.orderData.Rtn_Qty || 0,
                returnLocation: state.orderData.Ret_Loc || state.orderData.Dlv_Loc || '',
                returnRemark: state.orderData.Rtn_Remark || '',
                returnTime: state.orderData.Ret_Time ? dayjs(state.orderData.Ret_Time) : dayjs()
            }));
        } else {
            toast.error('No order data found');
            navigate('/TiffinReturnTable');
        }
    }, [location.state, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'returnQuantity') {
            const newValue = parseInt(value) || 0;
            if (newValue > orderData?.Req_Qty) {
                setSnackbar({
                    open: true,
                    message: `Return quantity cannot exceed total delivered quantity (${orderData.Req_Qty})`,
                    severity: 'error'
                });
                return;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleReturnCollection = async () => {
        setSubmitting(true);
        try {
            const UserId = sessionStorage?.getItem('UserId');
            if (!UserId) {
                setSnackbar({ open: true, message: 'User not found. Please login again.', severity: 'error' });
                return;
            }

            const payload = {
                Tfn_ReqId: orderData.Tfn_ReqId,
                Ret_LoginID: UserId,
                Ret_Time: formData.returnTime.format('YYYY-MM-DD HH:mm:ss'),
                Bulk_Rtn: formData.returnQuantity > 0 ? "Y" : "N",
                Rtn_Qty: parseInt(formData.returnQuantity),
                Rtn_Remark: formData.returnRemark || (formData.returnQuantity > 0 ? "Empty tiffins collected" : "No tiffins to return"),
                UpdatedBy: parseInt(UserId)
            };

            const response = await axiosInstance.post('TrnTiffin/UpdateTrnTiffinReturnStatus', payload);

            if (response.data?.Status === 0 || response.data?.Success) {
                toast.success('Empty tiffins collected successfully!');
                setTimeout(() => {
                    navigate('/TiffinReturnTable');
                }, 2000);
            } else {
                throw new Error(response.data?.Message || 'Failed to collect empty tiffins');
            }
        } catch (err) {
            console.error('Error collecting returns:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.Message || err.message || 'Failed to collect empty tiffins. Please try again.',
                severity: 'error'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formatTimeOnly = (dateTimeString) => {
        if (!dateTimeString) return 'N/A';
        const date = new Date(dateTimeString);
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                minHeight: '100vh', 
                bgcolor: '#f5f7fa', 
                p: { xs: 1, sm: 2, md: 3, lg: 4 },
                position: 'relative'
            }}>
                <ToastContainer 
                    position={isMobile ? "bottom-center" : "top-right"}
                    style={{ fontSize: isMobile ? '12px' : '14px' }}
                />
                
                {/* Header Section */}
                <Box sx={{ maxWidth: 1400, mx: 'auto', mb: { xs: 2, sm: 3 } }}>
                    <Paper elevation={0} sx={{ 
                        p: { xs: 1.5, sm: 2 }, 
                        borderRadius: { xs: 2, sm: 3 }, 
                        bgcolor: 'white', 
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)' 
                    }}>
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 }
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                                <IconButton 
                                    onClick={() => navigate('/TiffinReturnTable')}
                                    sx={{ 
                                        bgcolor: '#f0f0f0', 
                                        '&:hover': { bgcolor: '#e0e0e0' },
                                        width: { xs: 32, sm: 40 },
                                        height: { xs: 32, sm: 40 }
                                    }}
                                >
                                    <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 24 } }} />
                                </IconButton>
                                <Box>
                                    <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                                        Empty Tiffin Collection
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                                        Collect empty tiffins from customer
                                    </Typography>
                                </Box>
                            </Box>
                            <Chip 
                                icon={<InventoryIcon />}
                                label="Return Collection Mode"
                                size={isMobile ? "small" : "medium"}
                                sx={{ 
                                    bgcolor: '#fef3c7',
                                    color: '#d97706',
                                    fontWeight: 600,
                                    px: 1,
                                    fontSize: { xs: '11px', sm: '13px' }
                                }}
                            />
                        </Box>
                    </Paper>
                </Box>

                <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ maxWidth: 1400, mx: 'auto' }}>
                    {/* Left Column - Order Details */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ 
                            borderRadius: { xs: 2, sm: 3 }, 
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            overflow: 'hidden',
                            position: { xs: 'relative', md: 'sticky' },
                            top: { md: 20 }
                        }}>
                            <Box sx={{ 
                                background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                                p: { xs: 2, sm: 3 },
                                color: 'white'
                            }}>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1, fontSize: { xs: '10px', sm: '12px' } }}>
                                    Return Collection
                                </Typography>
                                <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, mb: 1 }}>
                                    #{orderData?.Tfn_ReqId}
                                </Typography>
                                <Chip 
                                    label={orderData?.Tfn_ReqSt}
                                    size="small"
                                    sx={{ 
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        color: 'white',
                                        fontWeight: 600
                                    }}
                                />
                            </Box>

                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <SchoolIcon sx={{ color: '#635bff', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Institute</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {orderData?.InstituteName}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <LocationOnIcon sx={{ color: '#f97316', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Collection Location</Typography>
                                        </Box>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {orderData?.Dlv_Loc}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <RestaurantIcon sx={{ color: '#d97706', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Meal Type</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {orderData?.MealTypeName}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                            <AccessTimeIcon sx={{ color: '#3b82f6', fontSize: { xs: 18, sm: 20 } }} />
                                            <Typography variant="body2" color="text.secondary">Delivery Time</Typography>
                                        </Box>
                                        <Typography variant="body1" fontWeight={500} sx={{ fontSize: { xs: '14px', sm: '16px' } }}>
                                            {formatTimeOnly(orderData?.Dlv_Time)}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">Delivered Quantity</Typography>
                                        <Typography variant="h5" fontWeight={700} sx={{ color: '#22c55e' }}>
                                            {orderData?.Dlv_Qty}
                                            <Typography component="span" variant="body2" color="text.secondary"> Tiffin(s)</Typography>
                                        </Typography>
                                    </Grid>

                                    {orderData?.Dlv_Remark && (
                                        <Grid item xs={12}>
                                            <Typography variant="body2" color="text.secondary">Delivery Remarks</Typography>
                                            <Typography variant="body2" sx={{ 
                                                bgcolor: '#f3f4f6', 
                                                p: 1.5, 
                                                borderRadius: 2,
                                                fontSize: { xs: '12px', sm: '14px' }
                                            }}>
                                                {orderData.Dlv_Remark}
                                            </Typography>
                                        </Grid>
                                    )}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Right Column - Return Collection Form */}
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: { xs: 2, sm: 3 }, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ mb: 3 }}>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <AlertTitle>Collection Instructions</AlertTitle>
                                        Collect empty tiffins from the customer. 
                                        Total delivered: <strong>{orderData?.Req_Qty}</strong> tiffins
                                    </Alert>
                                </Box>

                                <Grid container spacing={2.5}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size={isMobile ? "small" : "medium"}
                                            label="Return Quantity"
                                            name="returnQuantity"
                                            type="number"
                                            value={formData.returnQuantity}
                                            onChange={handleChange}
                                           inputProps={{ min: 0, max: orderData?.Dlv_Qty }}
                                           helperText={`Max: ${orderData?.Dlv_Qty} tiffin(s)`} 
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <MobileDateTimePicker
                                            label="Collection Time"
                                            value={formData.returnTime}
                                            onChange={(newTime) => setFormData(prev => ({ ...prev, returnTime: newTime }))}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    size: isMobile ? "small" : "medium"
                                                }
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size={isMobile ? "small" : "medium"}
                                            label="Collection Location"
                                            name="returnLocation"
                                            value={formData.returnLocation}
                                            onChange={handleChange}
                                            disabled
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size={isMobile ? "small" : "medium"}
                                            label="Return Remarks"
                                            name="returnRemark"
                                            value={formData.returnRemark}
                                            onChange={handleChange}
                                            multiline
                                            rows={3}
                                            placeholder="Any issues with the returned tiffins..."
                                        />
                                    </Grid>

                                    {formData.returnQuantity < orderData?.Req_Qty && formData.returnQuantity > 0 && (
                                        <Grid item xs={12}>
                                            <Alert severity="warning" icon={<WarningAmberIcon />}>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                                                    <strong>Partial Collection:</strong> You are collecting only {formData.returnQuantity} out of {orderData.Req_Qty} empty tiffins.
                                                </Typography>
                                            </Alert>
                                        </Grid>
                                    )}

                                    {formData.returnQuantity === 0 && (
                                        <Grid item xs={12}>
                                            <Alert severity="info" icon={<CheckCircleIcon />}>
                                                <Typography variant="body2" sx={{ fontSize: { xs: '11px', sm: '13px' } }}>
                                                    No empty tiffins to collect at this time. You can still submit with quantity 0.
                                                </Typography>
                                            </Alert>
                                        </Grid>
                                    )}

                                    <Grid item xs={12}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={handleReturnCollection}
                                            disabled={submitting}
                                            size={isMobile ? "large" : "medium"}
                                            sx={{
                                                py: { xs: 1, sm: 1.5 },
                                                bgcolor: '#f59e0b',
                                                '&:hover': { bgcolor: '#d97706' }
                                            }}
                                        >
                                            {submitting ? <CircularProgress size={24} /> : '📦 Confirm Collection'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={5000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ 
                        vertical: isMobile ? 'bottom' : 'top', 
                        horizontal: isMobile ? 'center' : 'right' 
                    }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default TiffinReturnForm;